import { useEffect, useMemo, useState } from "react";
import { splitUnitItems } from "@/lib/unitPlacement";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { Dialog } from "@/components/modals";
import StudentActivityPreview from "@/components/student/StudentActivityPreview";
import type { QuestionRecord } from "@/components/courses/QuestionEditor";
import { api, errorMessage } from "@/lib/api";
import {
  activityHasQuestions,
  activityIncludes,
  activityRequireKnowledgeOn,
  assignmentPointsOf,
  incompleteReasons,
  partsFromBody,
} from "@/lib/activityAnatomy";
import { inferSlideMode, normalizeSlideDecks } from "@/lib/slideDecks";
import "@/styles/student.css";
import "./StudentPreviewDialog.css";
import "./CoursePreviewDialog.css";

type PreviewItem = {
  id: string;
  type: string;
  label: string;
  title: string | null;
  body: unknown;
  content: Record<string, unknown>;
  metadata: Record<string, unknown>;
  estimatedMinutes: number;
  timeLimitMinutes: number;
  maxAttempts: number | null;
  dueAt: string | null;
  questionCount: number;
  questionsIncomplete?: number;
  status?: string;
  published: boolean;
};

type PreviewContainer = {
  typeLabel: string;
  title: string | null;
  activities: PreviewItem[];
  children: PreviewContainer[];
};

type PreviewStructure = {
  course: { title: string };
  containers: PreviewContainer[];
  activities: PreviewItem[];
};

type Stop = { item: PreviewItem; trail: string };

function collectStops(structure: PreviewStructure): Stop[] {
  const out: Stop[] = [];
  const walk = (nodes: PreviewContainer[], trail: string) => {
    for (const node of nodes) {
      const name = node.title?.trim() || `Untitled ${node.typeLabel}`;
      const here = trail ? `${trail} / ${name}` : name;
      if (node.children.length) {
        // A unit: its own items sit above or below its lessons, as placed.
        const { before, after } = splitUnitItems(node.activities);
        for (const item of before) out.push({ item, trail: here });
        walk(node.children, here);
        for (const item of after) out.push({ item, trail: here });
      } else {
        for (const item of node.activities) out.push({ item, trail: here });
      }
    }
  };
  walk(structure.containers, "");
  for (const item of structure.activities) {
    out.push({ item, trail: structure.course.title?.trim() || "Course" });
  }
  return out;
}

function gapNotes(item: PreviewItem): string[] {
  const reasons = incompleteReasons({
    type: item.type,
    body: item.body,
    content: item.content,
    metadata: item.metadata,
    questionCount: item.questionCount,
  });
  const n = Number(item.questionsIncomplete) || 0;
  if (n > 0 && !reasons.some((row) => /questions? are still incomplete|question is still incomplete/.test(row))) {
    reasons.push(n === 1 ? "1 question is still incomplete." : `${n} questions are still incomplete.`);
  }
  return reasons;
}

function itemTitle(item: PreviewItem) {
  return item.title?.trim() || item.label || "Untitled";
}

export default function CoursePreviewDialog({
  open,
  structure,
  onClose,
}: {
  open: boolean;
  structure: PreviewStructure;
  onClose: () => void;
}) {
  const stops = useMemo(() => collectStops(structure), [structure]);
  const [index, setIndex] = useState(0);
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");
  const [questions, setQuestions] = useState<QuestionRecord[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState("");

  useEffect(() => {
    if (!open) return;
    setIndex(0);
    setViewport("desktop");
  }, [open]);

  const stop = stops[Math.min(index, Math.max(stops.length - 1, 0))] || null;
  const item = stop?.item || null;
  const hasQuestions = item
    ? activityHasQuestions(item.type, item.metadata as { hasQuestions?: unknown })
    : false;

  useEffect(() => {
    if (!open || !item || !hasQuestions) {
      setQuestions([]);
      setQuestionsLoading(false);
      setQuestionsError("");
      return;
    }
    let cancelled = false;
    setQuestionsLoading(true);
    setQuestionsError("");
    setQuestions([]);
    api<{ success: boolean; data?: QuestionRecord[] }>(`/api/activities/${item.id}/questions`)
      .then((envelope) => {
        if (cancelled) return;
        setQuestions(Array.isArray(envelope.data) ? envelope.data : []);
      })
      .catch((err) => {
        if (!cancelled) setQuestionsError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setQuestionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, item, hasQuestions]);

  const gaps = item ? gapNotes(item) : [];
  const content = (item?.content || {}) as Record<string, unknown>;
  const decks = normalizeSlideDecks(content.decks);
  const terms = (Array.isArray(content.terms) ? content.terms : [])
    .filter((row): row is { id?: unknown; term?: unknown; definition?: unknown } => Boolean(row) && typeof row === "object")
    .map((row, i) => ({
      id: String(row.id || i),
      term: String(row.term || ""),
      definition: String(row.definition || ""),
    }));

  return (
    <Dialog
      open={open}
      title="Course preview"
      subtitle="Nothing here is saved. Answers and submissions stay off."
      width={viewport === "mobile" ? "min(40rem, calc(100vw - 2rem))" : "min(68rem, calc(100vw - 2rem))"}
      maxHeight="min(90dvh, 52rem)"
      onClose={onClose}
      headerActions={
        <div className="al-cb-preview-tabs" role="tablist" aria-label="Preview size">
          <button
            type="button"
            role="tab"
            aria-selected={viewport === "desktop"}
            className={`al-cb-preview-tab${viewport === "desktop" ? " al-cb-preview-tab--on" : ""}`}
            onClick={() => setViewport("desktop")}
          >
            Desktop
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewport === "mobile"}
            className={`al-cb-preview-tab${viewport === "mobile" ? " al-cb-preview-tab--on" : ""}`}
            onClick={() => setViewport("mobile")}
          >
            Mobile
          </button>
        </div>
      }
    >
      {stops.length === 0 ? (
        <p className="al-course-preview-empty">Nothing in this course yet. Add an activity, then preview it here.</p>
      ) : (
        <div className="al-course-preview">
          <ol className="al-course-preview-rail" aria-label="Activities">
            {stops.map((row, i) => (
              <li key={row.item.id}>
                <button
                  type="button"
                  className={`al-course-preview-stop${i === index ? " is-on" : ""}`}
                  aria-current={i === index ? "true" : undefined}
                  onClick={() => setIndex(i)}
                >
                  <span className="al-course-preview-stop-title">{itemTitle(row.item)}</span>
                  <span className="al-course-preview-stop-trail">{row.trail}</span>
                </button>
              </li>
            ))}
          </ol>
          {item && stop ? (
            <div className="al-course-preview-stage">
              <div className="al-course-preview-scroll">
              <p className="al-course-preview-note">
                <Eye size={14} aria-hidden />
                Preview only. This does not record progress or accept work.
              </p>
              {item.status === "proposed" ? (
                <p className="al-course-preview-warn">Proposed. Students do not see this until it is applied.</p>
              ) : null}
              {!item.published && item.status !== "proposed" ? (
                <p className="al-course-preview-warn">Not published. Students do not see this yet.</p>
              ) : null}
              {gaps.length ? (
                <p className="al-course-preview-warn">
                  Not finished yet. {gaps.join(" ")}
                </p>
              ) : null}
              {questionsError ? <p className="al-course-preview-warn">{questionsError}</p> : null}
              <div
                className={`al-cb-preview-stage al-cb-preview-stage--page${
                  viewport === "mobile" ? " al-cb-preview-stage--mobile" : ""
                }`}
              >
                <div className="acuity-student acuity-student--preview">
                  <StudentActivityPreview
                    type={item.type}
                    title={itemTitle(item)}
                    estimatedMinutes={Number(item.estimatedMinutes) || 0}
                    dueAt={item.dueAt || ""}
                    timeLimitMinutes={Number(item.timeLimitMinutes) || 0}
                    maxAttempts={item.maxAttempts == null ? "" : Number(item.maxAttempts)}
                    includes={activityIncludes(item.type, item.metadata as { includes?: unknown }, {
                      dueAt: item.dueAt,
                      timeLimitMinutes: item.timeLimitMinutes,
                      maxAttempts: item.maxAttempts,
                    })}
                    parts={partsFromBody(item.body)}
                    questions={questions}
                    questionsLoading={questionsLoading}
                    hasQuestions={hasQuestions}
                    requireKnowledge={activityRequireKnowledgeOn(item.metadata as { requireKnowledge?: unknown })}
                    terms={terms}
                    decks={decks}
                    slideMode={inferSlideMode(content.slideMode, decks)}
                    assignmentPoints={assignmentPointsOf(item.type, item.metadata as { assignmentPoints?: unknown; hasQuestions?: unknown })}
                  />
                </div>
              </div>
              </div>
              <div className="al-course-preview-nav">
                <button type="button" className="al-cb-btn al-cb-btn--ghost" disabled={index <= 0} onClick={() => setIndex((n) => Math.max(0, n - 1))}>
                  <ChevronLeft size={14} aria-hidden />
                  Previous
                </button>
                <span>
                  {index + 1} of {stops.length}
                </span>
                <button
                  type="button"
                  className="al-cb-btn al-cb-btn--ghost"
                  disabled={index >= stops.length - 1}
                  onClick={() => setIndex((n) => Math.min(stops.length - 1, n + 1))}
                >
                  Next
                  <ChevronRight size={14} aria-hidden />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
